/**
 * Modal Component
 *
 * Compound component for centered dialogs, bottom sheets, and top-docked search panels.
 * Mirrors the Drawer API pattern. All a11y baked in: focus trap, ESC, body scroll lock, ARIA.
 *
 * Variants:
 *   - centered: classic centered dialog (all screen sizes)
 *   - sheet: bottom sheet on mobile (<768px), centered on desktop
 *   - search: top-docked panel (slides down from top)
 */

import {
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useState,
  useContext,
  createContext,
  useMemo,
} from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { lockBodyScroll, unlockBodyScroll } from '../utils/body-scroll-lock';
import { defaultIconProps } from '../atoms/Icon';

/* ── Types ─────────────────────────────────────────────────────────────────── */

export type ModalVariant = 'centered' | 'sheet' | 'search';
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  variant?: ModalVariant;
  size?: ModalSize;
  isOpen: boolean;
  onClose: () => void;
  backdropColor?: string;
  backgroundColor?: string;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  id?: string;
  children?: ReactNode;
}

interface ModalContextValue {
  titleId: string;
  onClose: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

/** Longest panel close animation (sheet mobile slideDown). */
const CLOSE_ANIMATION_MS = 300;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ── Size map ──────────────────────────────────────────────────────────────── */

const SIZE_MAP: Record<ModalSize, string> = {
  sm: '420px',
  md: '560px',
  lg: '680px',
  xl: '860px',
};

/* ── Keyframes ─────────────────────────────────────────────────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) translateY(0);
  }
`;

const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96) translateY(10px);
  }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

const searchSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const searchSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%);
  }
`;

/* ── Styled: Backdrop ──────────────────────────────────────────────────────── */

interface BackdropStyleProps {
  isClosing: boolean;
  backdropColor: string;
}

const Backdrop = styled.div<BackdropStyleProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ backdropColor }) => backdropColor};
  z-index: 99998;
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.2s ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01ms;
  }
`;

/* ── Styled: Panel (variant-aware) ─────────────────────────────────────────── */

interface PanelStyleProps {
  variant: ModalVariant;
  maxWidth: string;
  isClosing: boolean;
  bg: string;
}

const centeredStyles = css`
  position: fixed;
  top: 50%;
  left: 50%;
  width: calc(100% - 32px);
  max-height: calc(100dvh - 64px);
  border-radius: 8px;
`;

const sheetMobileStyles = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-height: 90vh;
  border-radius: 16px 16px 0 0;
`;

const sheetDesktopStyles = css`
  position: fixed;
  top: 50%;
  left: 50%;
  bottom: auto;
  right: auto;
  width: calc(100% - 32px);
  max-height: calc(100dvh - 64px);
  border-radius: 16px;
`;

const searchStyles = css`
  position: fixed;
  top: 0;
  left: 50%;
  width: calc(100% - 32px);
  max-height: 70vh;
  border-radius: 0 0 12px 12px;
  padding-top: 56px;

  @media (min-width: 768px) {
    padding-top: 80px;
  }
`;

const Panel = styled.div<PanelStyleProps>`
  z-index: 99999;
  background: ${({ bg }) => bg};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1);
  max-width: ${({ maxWidth }) => maxWidth};

  /* Variant: centered */
  ${({ variant }) => variant === 'centered' && centeredStyles}
  ${({ variant, isClosing }) =>
    variant === 'centered' &&
    css`
      animation: ${isClosing ? scaleOut : scaleIn} 0.2s ease-out forwards;
    `}

  /* Variant: sheet — mobile */
  ${({ variant }) => variant === 'sheet' && sheetMobileStyles}
  ${({ variant, isClosing }) =>
    variant === 'sheet' &&
    css`
      animation: ${isClosing ? slideDown : slideUp} 0.3s ease-out forwards;
    `}

  /* Variant: sheet — desktop override */
  @media (min-width: 768px) {
    ${({ variant }) => variant === 'sheet' && sheetDesktopStyles}
    ${({ variant, isClosing }) =>
      variant === 'sheet' &&
      css`
        animation: ${isClosing ? scaleOut : scaleIn} 0.2s ease-out forwards;
      `}
  }

  /* Variant: search */
  ${({ variant }) => variant === 'search' && searchStyles}
  ${({ variant, isClosing }) =>
    variant === 'search' &&
    css`
      animation: ${isClosing ? searchSlideOut : searchSlideIn} 0.2s ease-out forwards;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01ms;
  }
`;

/* ── Focus trap ────────────────────────────────────────────────────────────── */

function useFocusTrap(panelRef: React.RefObject<HTMLDivElement | null>, isOpen: boolean) {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = panelRef.current.querySelectorAll<HTMLElement>(focusableSelector);
    if (focusables.length > 0) {
      focusables[0].focus();
    }

    return () => {
      previousFocusRef.current?.focus();
    };
  }, [isOpen, panelRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusableSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [panelRef],
  );

  return handleKeyDown;
}

/* ── Modal (root) ──────────────────────────────────────────────────────────── */

let modalIdCounter = 0;

export const Modal = ({
  variant = 'centered',
  size = 'md',
  isOpen,
  onClose,
  backdropColor = 'rgba(0, 0, 0, 0.5)',
  backgroundColor,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  id,
  children,
}: ModalProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const autoId = useRef(`modal-${++modalIdCounter}`);
  const modalId = id || autoId.current;
  const titleId = `${modalId}-title`;

  const handleFocusTrapKeyDown = useFocusTrap(panelRef, shouldRender && !isClosing);

  const finishClose = useCallback(() => {
    setShouldRender(false);
    setIsClosing(false);
  }, []);

  // Open/close lifecycle
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShouldRender(true);
      return;
    }
    if (!shouldRender) return;

    if (prefersReducedMotion()) {
      finishClose();
      return;
    }
    setIsClosing(true);
  }, [isOpen, shouldRender, finishClose]);

  // Fallback when animationend never fires (jsdom / missed events)
  useEffect(() => {
    if (!isClosing) return;
    const timer = window.setTimeout(finishClose, CLOSE_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [isClosing, finishClose]);

  // Body scroll lock
  useEffect(() => {
    if (!shouldRender || isClosing) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [shouldRender, isClosing]);

  // ESC key
  useEffect(() => {
    if (!shouldRender || isClosing || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [shouldRender, isClosing, closeOnEscape, onClose]);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      finishClose();
    }
  }, [isClosing, finishClose]);

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdropClick) {
      onClose();
    }
  }, [closeOnBackdropClick, onClose]);

  const handlePanelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const contextValue = useMemo(
    () => ({ titleId, onClose }),
    [titleId, onClose],
  );

  if (!shouldRender) return null;
  if (typeof document === 'undefined') return null;

  const bg = backgroundColor || '#ffffff';

  return createPortal(
    <ModalContext.Provider value={contextValue}>
      <Backdrop
        isClosing={isClosing}
        backdropColor={backdropColor}
        onClick={handleBackdropClick}
        data-testid="modal-backdrop"
      />
      <Panel
        ref={panelRef}
        variant={variant}
        maxWidth={SIZE_MAP[size]}
        isClosing={isClosing}
        bg={bg}
        onClick={handlePanelClick}
        onKeyDown={handleFocusTrapKeyDown}
        onAnimationEnd={handleAnimationEnd}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="modal-panel"
      >
        {children}
      </Panel>
    </ModalContext.Provider>,
    document.body,
  );
};

export default Modal;

/* ── Sub-components ────────────────────────────────────────────────────────── */

interface ModalHeaderProps {
  title: string;
  onClose?: () => void;
  /** Internal: auto-set by parent context. Consumers can override for aria wiring. */
  titleId?: string;
  /** Disable the close button (e.g. while async work is in flight). */
  closeDisabled?: boolean;
}

export const ModalHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space?.lg || '1.5rem'} 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border?.color || '#e5e5e5'};
`;

const ModalTitleText = styled.h2`
  font-size: ${({ theme }) => `${theme.fontSizes?.[3] || 16}px`};
  font-weight: ${({ theme }) => theme.typography?.config?.weight?.bold || 700};
  color: ${({ theme }) => theme.colors?.text?.primary || '#292929'};
  margin: 0;
`;

export const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space?.xs || '0.25rem'};
  color: ${({ theme }) => theme.colors?.text?.secondary || '#808080'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors?.text?.primary || '#292929'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * ModalHeader — title + close button.
 *
 * Auto-wires `titleId` and `onClose` from Modal context when used inside `<Modal>`.
 * Pass props explicitly to override.
 */
export const ModalHeader = ({
  title,
  onClose,
  titleId,
  closeDisabled = false,
}: ModalHeaderProps) => {
  const ctx = useContext(ModalContext);
  const resolvedTitleId = titleId ?? ctx?.titleId;
  const resolvedOnClose = onClose ?? ctx?.onClose;

  return (
    <ModalHeaderContainer>
      <ModalTitleText id={resolvedTitleId}>{title}</ModalTitleText>
      {resolvedOnClose && (
        <ModalCloseButton
          type="button"
          onClick={resolvedOnClose}
          disabled={closeDisabled}
          aria-label="Close"
          data-testid="modal-close-button"
        >
          <X {...defaultIconProps} size={20} />
        </ModalCloseButton>
      )}
    </ModalHeaderContainer>
  );
};

export const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.space?.md || '1rem'} 24px;
  -webkit-overflow-scrolling: touch;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space?.md || '1rem'};
  padding: ${({ theme }) => theme.space?.lg || '1.5rem'} 24px;
  border-top: 1px solid ${({ theme }) => theme.colors?.border?.color || '#e5e5e5'};
`;

export const ModalPrimaryButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space?.md || '1rem'};
  font-size: ${({ theme }) => `${theme.fontSizes?.[2] || 14}px`};
  font-weight: ${({ theme }) => theme.typography?.config?.weight?.demi || 600};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors?.pallete?.secondary?.color || '#292929'};
  color: ${({ theme }) => theme.colors?.pallete?.secondary?.contrast || '#ffffff'};
  border: none;
  border-radius: ${({ theme }) => theme.radius?.small || '4px'};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalOutlineButton = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.space?.md || '1rem'};
  font-size: ${({ theme }) => `${theme.fontSizes?.[2] || 14}px`};
  font-weight: ${({ theme }) => theme.typography?.config?.weight?.demi || 600};
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors?.background?.primary || '#ffffff'};
  color: ${({ theme }) => theme.colors?.text?.primary || '#292929'};
  border: 2px solid ${({ theme }) => theme.colors?.text?.primary || '#292929'};
  border-radius: ${({ theme }) => theme.radius?.small || '4px'};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors?.background?.secondary || '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
